import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Add,
  Visibility,
  Search,
  MoreVert,
  Block,
  Person,
  Email,
  Phone,
  Badge,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useCustomers, usePermission } from '../hooks';
import { colors } from '../theme/theme';
import { customerService } from '../services';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';

export const CustomersIndividualsPage = () => {
  const { can } = usePermission();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const { customers, isLoading, error } = useCustomers({
    page: page + 1,
    per_page: rowsPerPage,
    search,
    type: 'individual',
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCustomer(null);
  };

  const handleViewDetails = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!selectedCustomer) {
      return;
    }

    const customerId = selectedCustomer.id;
    handleMenuClose();

    // Navigate after a small delay to ensure menu closes properly
    setTimeout(() => {
      navigate(`/customers/${customerId}`);
    }, 0);
  };

  const handleSuspend = async () => {
    if (!selectedCustomer) return;

    const action = selectedCustomer.status === 'suspended' ? 'activate' : 'suspend';
    const confirmMessage = `Are you sure you want to ${action} this customer?`;

    if (!window.confirm(confirmMessage)) {
      handleMenuClose();
      return;
    }

    try {
      await customerService.toggleSuspend(selectedCustomer.id);
      showSnackbar(`Customer ${action}d successfully`, 'success');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      handleMenuClose();
    } catch (error) {
      showSnackbar(`Failed to ${action} customer`, 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => `${row.first_name || ''} ${row.last_name || ''}`,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person sx={{ fontSize: 18, color: colors.grey[600] }} />
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
      valueGetter: (value) => value || 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email sx={{ fontSize: 16, color: colors.grey[500] }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone sx={{ fontSize: 16, color: colors.grey[500] }} />
          <Typography variant="body2">{params.value || 'N/A'}</Typography>
        </Box>
      ),
    },
    {
      field: 'id_number',
      headerName: 'ID Number',
      width: 140,
      renderCell: (params) => (
        <Chip
          icon={<Badge sx={{ fontSize: 14 }} />}
          label={params.value || 'N/A'}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const isActive = params.value !== 'suspended';
        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? 'Active' : 'Suspended'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Individual Customers"
          subtitle="Manage individual customer accounts"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Customers', path: '/customers' },
            { label: 'Individuals' },
          ]}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load customers. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && customers && (
          <Grid container spacing={3} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Individuals"
                value={customers.meta?.total?.toLocaleString() || 0}
                icon={<Person />}
                color={colors.primary.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Active Accounts"
                value={customers.data?.filter((c: any) => c.status !== 'suspended').length || 0}
                icon={<CheckCircle />}
                color={colors.success.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Suspended"
                value={customers.data?.filter((c: any) => c.status === 'suspended').length || 0}
                icon={<Cancel />}
                color={colors.error.main}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Showing"
                value={customers.data?.length || 0}
                icon={<Badge />}
                color={colors.info.main}
              />
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.grey[200]}`, backgroundColor: colors.grey[50] }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, phone, ID number..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={customers?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize: rowsPerPage }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setRowsPerPage(model.pageSize);
                  }}
                  rowCount={customers?.meta?.total || 0}
                  paginationMode="server"
                  disableRowSelectionOnClick
                  autoHeight
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontWeight: 600,
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                    },
                    '& .MuiTablePagination-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={(e) => handleViewDetails(e)}>
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          {can('customers.manage') && <MenuItem onClick={handleSuspend} sx={{ color: colors.warning.main }}>
            <ListItemIcon>
              <Block fontSize="small" sx={{ color: colors.warning.main }} />
            </ListItemIcon>
            <ListItemText>
              {selectedCustomer?.status === 'suspended' ? 'Activate' : 'Suspend'}
            </ListItemText>
          </MenuItem>}
        </Menu>
      </Box>
    </AdminLayout>
  );
};
